import { typeInfo, bookBadgeOf } from "../../constants/typeInfo";
import { formatScore } from "../../utils/textUtils";

const WEEKDAY_LIST = ["일", "월", "화", "수", "목", "금", "토"];

export const formatTodayText = (date = new Date()) =>
  `${date.getMonth() + 1}월 ${date.getDate()}일 ${WEEKDAY_LIST[date.getDay()]}요일`;

export const formatShortDate = (value) => {
  if (!value) return "";
  const date = new Date(value);
  return `${date.getMonth() + 1}.${date.getDate()}`;
};

export const formatDueText = (value) => {
  if (!value) return "";
  const date = new Date(value);
  return `${date.getMonth() + 1}.${date.getDate()} (${WEEKDAY_LIST[date.getDay()]})`;
};

// 부를 때 붙이는 말. 성을 떼고 받침이 있으면 '아', 없으면 '야'(지우야, 서준아).
// 한글 이름이 아니면 이름 그대로 부른다.
export const callNameOf = (fullName) => {
  const name = String(fullName || "").trim();
  if (!name) return "";
  const givenName = name.length >= 3 ? name.slice(1) : name;
  const lastCode = givenName.charCodeAt(givenName.length - 1) - 0xac00;
  if (lastCode < 0 || lastCode > 11171) return name;
  return `${givenName}${lastCode % 28 === 0 ? "야" : "아"}`;
};

const startOfDay = (value) => {
  const date = new Date(value);
  date.setHours(0, 0, 0, 0);
  return date;
};

// 마감까지 남은 날. 미완료 과제 목록(AssignmentTable)과 같은 기준이다.
export const ddayOf = (closedAt) => {
  if (!closedAt) return null;
  const diff = Math.round((startOfDay(closedAt) - startOfDay(new Date())) / 86400000);
  if (diff < 0) return { text: "기한 지남", tone: "over" };
  if (diff === 0) return { text: "오늘 마감", tone: "hot" };
  if (diff <= 2) return { text: `D-${diff}`, tone: "hot" };
  return { text: `D-${diff}`, tone: "calm" };
};

export const kindLabelOf = (item) => {
  if (item.kindLabel) return item.kindLabel;
  if (item.type === "class_content") return item.class_content_data?.typeName || "수업";
  return bookBadgeOf(item.type, item.title)?.text || typeInfo[item.type]?.text || "과제";
};

export const kindColorOf = (type) => ({
  color: typeInfo[type]?.color || "#3A434E",
  backgroundColor: typeInfo[type]?.backgroundColor || "#F4F8F7",
});

// 서버(AssignmentListForStudentViewSet)와 같은 순서: 아직 낼 수 있는 것을 마감 가까운 순으로,
// 기한이 지난 것은 그 뒤에 최근 것부터. 목록 thunk 가 지필과제와 합치며 출제일 순으로 다시 섞어서 여기서 되돌린다.
export const sortUnfinished = (itemList) => {
  const now = Date.now();
  const dueOf = (item) => (item.closed_at ? new Date(item.closed_at).getTime() : null);
  const isOver = (item) => dueOf(item) !== null && dueOf(item) < now;
  return [...itemList].sort((first, second) => {
    if (isOver(first) !== isOver(second)) return isOver(first) ? 1 : -1;
    const firstDue = dueOf(first);
    const secondDue = dueOf(second);
    if (firstDue === null || secondDue === null) return firstDue === null ? 1 : -1;
    return isOver(first) ? secondDue - firstDue : firstDue - secondDue;
  });
};

export const finishedDateOf = (item) => item.finished_at || item.submitted_at || item.created_at;

// 끝낸 과제의 점수. 완료 과제 목록(FinishedAssignmentTable)과 같은 값을 쓴다.
export const scoreOf = (item) => {
  if (item.type === "class_content") {
    if (item.score === null || item.score === undefined) return null;
    return { text: `${formatScore(item.score)}/${formatScore(item.totalScore || 100)}`, rate: (item.score / (item.totalScore || 100)) * 100 };
  }
  const value = item.type === "paper_assignment" ? item.score : item.grade;
  if (value === null || value === undefined) return null;
  return { text: `${formatScore(value)}점`, rate: Number(value) };
};

export const LOW_SCORE_RATE = 70;
export const WEAK_AREA_RATE = 60;

// 할 일 버튼 글자. 미완료 과제 목록과 같은 말이다.
export const actionTextOf = (item) => {
  if (item.isLevelTestReview) return "시작하기";
  if (item.type === "paper_assignment") return "과제 올리기";
  return Number(item.progress_rate) > 0 ? "이어하기" : "시작하기";
};

// 레벨테스트 복습(ECHO 계통)을 과제와 같은 줄에 세우려고 모양만 맞춘다. 누르면 start_url 로 간다.
export const levelTestReviewToItem = (review) => ({
  id: `level_review_${review.key}`,
  isLevelTestReview: true,
  type: "level_test_review",
  title: review.title,
  kindLabel: `레벨테스트 복습 · ${review.kind_label}`,
  closed_at: review.due_at,
  progress_rate: 0,
  start_url: review.start_url,
});

export const retryTextOf = (item) => {
  if (!item.is_retry) return "";
  const count = item.retry_info?.retry_count;
  return count ? `복습 ${count}회차` : "복습";
};

const hasBatchim = (text) => {
  const code = String(text || "").charCodeAt(String(text || "").length - 1) - 0xac00;
  return code >= 0 && code <= 11171 && code % 28 !== 0;
};

// '지우가 할 것', '서준이가 할 것'
export const doerNameOf = (fullName) => {
  const name = String(fullName || "").trim();
  const givenName = name.length >= 3 ? name.slice(1) : name;
  return hasBatchim(givenName) ? `${givenName}이가` : `${givenName}가`;
};

// 영어 · 수학 가르기. 수학 과제는 종류가 math_ 로 시작한다
export const SUBJECT_ENGLISH = "english";
export const SUBJECT_MATH = "math";
export const subjectOf = (item) => (String(item.type || "").startsWith("math_") ? SUBJECT_MATH : SUBJECT_ENGLISH);
// 알림장 쪽(지필 · 수업 결과)은 과목을 모른다. 어느 탭에서나 보인다
export const isSubjectNeutral = (item) => item.type === "paper_assignment" || item.type === "class_content";
export const filterBySubject = (itemList, subject) =>
  subject ? itemList.filter((item) => isSubjectNeutral(item) || subjectOf(item) === subject) : itemList;

// 학원 테마 색(2026-11-03 대표 "초등 중등 다"). 서버가 로고에서 뽑아 student.academy_color 로 준다
// (api/user/academy_color.py). accent 는 흰 바탕 대비 4.5:1 이 나오는 색이라 글자 · 단추 · 막대 · 초등 왼쪽 메뉴에 그대로 쓴다.
// 노랑 로고는 서버가 accent 를 먹색으로 준다. 색이 없으면 Home.css 의 기본 먹색이 그대로 남는다.
export const academyThemeStyleOf = (student) => {
  const accent = student?.academy_color?.accent;
  if (!accent) return undefined;
  return { "--home-accent": accent };
};
